import request from '@/src/apis/axios';
import {
  DeleteMagazineByIdRequestBody,
  GetMagazineByIdRequestParams,
  GetMagazineViewHistoriesByIdRequestParams,
  GetMagazinesRequestParams,
  PostMagazineRequestBody,
  PutMagazineRequestBody,
  GetMagazineViewCustomerHistoriesRequestParams,
} from '@/src/apis/rest/magazine.dtos';
import {
  MagazineDto,
  MagazineViewCustomerHistoryDto,
} from '@/src/models/MagazineDto';
import MagazineViewHistoryDto from '@/src/models/MagazineViewHistoryDto';
import { ListResponse, ObjectResponse } from '@/src/types/common';
import { plainToInstance } from 'class-transformer';

const URL = {
  magazine: '/admin/magazines',
  magazineDetail: '/admin/magazines/{id}',
  magazineViewHistories: '/admin/magazines/{id}/view-history',
  magazineViewHistoriesExcel: '/admin/magazines/{id}/view-history/excel/export',
  magazineCustomerHistories: '/admin/magazine-view-histories',
  magazineExcel: '/admin/magazines/excel/export',
};

// 매거진 리스트 조회
export const getMagazinesAPI = async (
  requestParams: Partial<GetMagazinesRequestParams>,
) => {
  const { page = 1, pageSize = 10, ...rest } = requestParams;

  const { data } = await request<ListResponse<MagazineDto>>({
    method: 'get',
    url: URL.magazine,
    queryParams: { ...rest, page, pageSize },
  });

  const rows = plainToInstance(MagazineDto, data.rows);

  return { rows, count: data.count };
};

// 매거진 상세 조회
export const getMagazineByIdAPI = async ({
  id,
}: Partial<GetMagazineByIdRequestParams>) => {
  const { data } = await request<ObjectResponse<MagazineDto>>({
    method: 'get',
    url: URL.magazineDetail.replace('{id}', String(id)),
  });

  return plainToInstance(MagazineDto, data.row);
};

// 매거진 조회 회원 리스트 조회
export const getMagazineViewHistoriesAPI = async (
  requestParams: Partial<GetMagazineViewHistoriesByIdRequestParams>,
) => {
  const { page = 1, pageSize = 10, id } = requestParams;

  const { data } = await request<ListResponse<MagazineViewHistoryDto>>({
    method: 'get',
    url: URL.magazineViewHistories.replace('{id}', String(id)),
    queryParams: { page, pageSize },
  });

  const rows = plainToInstance(MagazineViewHistoryDto, data.rows);

  return { rows, count: data.count };
};

export const getMagazineCustomerHistoriesAPI = async (
  requestParams: Partial<GetMagazineViewCustomerHistoriesRequestParams>,
) => {
  const { page = 1, pageSize = 10, customerId } = requestParams;

  const { data } = await request<ListResponse<MagazineViewCustomerHistoryDto>>({
    method: 'get',
    url: URL.magazineCustomerHistories,
    queryParams: { page, pageSize, customerId },
  });

  const rows = plainToInstance(MagazineViewCustomerHistoryDto, data.rows);

  return { rows, count: data.count };
};

// 매거진 조회 회원 리스트 엑셀 다운로드
export const getMagazineViewHistoriesExcelAPI = async (
  password: string,
  requestParams: Partial<GetMagazineViewHistoriesByIdRequestParams>,
) => {
  const { data } = await request<Blob>({
    method: 'get',
    url: URL.magazineViewHistoriesExcel.replace(
      '{id}',
      String(requestParams.id),
    ),
    queryParams: { password },
    config: { responseType: 'blob' },
  });

  return data;
};

// 매거진 생성
export const postMagazineAPI = async (requestBody: PostMagazineRequestBody) => {
  const { data } = await request<ObjectResponse<MagazineDto>>({
    method: 'post',
    url: URL.magazine,
    requestBody: { ...requestBody },
  });

  return plainToInstance(MagazineDto, data.row);
};

// 매거진 수정
export const putMagazineAPI = async (requestBody: PutMagazineRequestBody) => {
  const { id, ...rest } = requestBody;

  const { data } = await request<ObjectResponse<MagazineDto>>({
    method: 'put',
    url: URL.magazineDetail.replace('{id}', String(id)),
    requestBody: { ...rest },
  });

  return plainToInstance(MagazineDto, data.row);
};

// 매거진 삭제
export const deleteMagazineByIdAPI = async ({
  id,
}: DeleteMagazineByIdRequestBody) => {
  const { data } = await request<ObjectResponse<MagazineDto>>({
    method: 'delete',
    url: URL.magazineDetail.replace('{id}', String(id)),
  });

  return plainToInstance(MagazineDto, data.row);
};

export const getMagazineExcelAPI = async (
  password: string,
  requestParams: Partial<GetMagazinesRequestParams>,
) => {
  const { data } = await request<Blob>({
    method: 'get',
    url: URL.magazineExcel,
    queryParams: { password, ...requestParams },
    config: { responseType: 'blob' },
  });

  return data;
};
